import React from 'react';
import { motion } from 'framer-motion';
import { Zap, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="beranda" 
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1710993244013-36a43c328cfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxmaWJlciUyMG9wdGljJTIwY2FibGUlMjBnbG93aW5nJTIwZnV0dXJpc3RpY3xlbnwwfHx8fDE3NzI1NDg0OTV8MA&ixlib=rb-4.1.0&q=85"
          alt="Fiber Optic Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/10 via-transparent to-brand-red/10" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Text */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-blue/10 border border-brand-blue/30 mb-6"
            >
              <Zap className="w-4 h-4 text-brand-blue" />
              <span className="text-sm font-medium text-brand-blue">Internet Tercepat di Polewali Mandar</span>
            </motion.div>

            {/* Heading */}
            <h1 className="font-outfit text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">
              Koneksi <span className="gradient-text-blue">Tanpa Batas</span> untuk{' '}
              <span className="gradient-text-red">Masa Depan</span>
            </h1>

            {/* Description */}
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-8 max-w-xl">
              Nikmati pengalaman internet super cepat dengan layanan fiber optik dari Kedai Teknologi Celebes. 
              Streaming, gaming, dan bekerja tanpa hambatan.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button 
                className="btn-primary px-8 py-4 rounded-full text-white font-semibold text-lg flex items-center gap-2"
                onClick={() => scrollToSection('#kontak')}
                data-testid="hero-cta-button"
              >
                Cek Ketersediaan
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                className="btn-secondary px-8 py-4 rounded-full font-semibold text-lg"
                onClick={() => scrollToSection('#paket')}
                data-testid="hero-secondary-button"
              >
                Lihat Paket
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t border-white/10">
              <div>
                <p className="text-3xl md:text-4xl font-outfit font-bold text-brand-blue">10K+</p>
                <p className="text-sm text-gray-500 mt-1">Pelanggan Aktif</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-outfit font-bold text-brand-red">99.9%</p>
                <p className="text-sm text-gray-500 mt-1">Uptime Jaringan</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-outfit font-bold text-white">24/7</p>
                <p className="text-sm text-gray-500 mt-1">Dukungan Teknis</p>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Floating Elements */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:flex justify-center items-center relative"
          >
            {/* Speed Indicator Card */}
            <motion.div 
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="glass rounded-2xl p-8 max-w-sm"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="icon-container">
                  <Zap className="w-6 h-6 text-brand-blue" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Kecepatan Hingga</p>
                  <p className="text-2xl font-outfit font-bold text-white">1 Gbps</p>
                </div>
              </div>
              
              {/* Speed Bars */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-16">Download</span>
                  <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: '95%' }}
                      transition={{ duration: 1.5, delay: 0.8 }}
                      className="h-full bg-gradient-to-r from-brand-blue to-brand-blue-neon rounded-full"
                    />
                  </div>
                  <span className="text-xs text-brand-blue font-medium">950 Mbps</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-16">Upload</span>
                  <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: '90%' }}
                      transition={{ duration: 1.5, delay: 1 }}
                      className="h-full bg-gradient-to-r from-brand-red to-brand-red-neon rounded-full"
                    />
                  </div>
                  <span className="text-xs text-brand-red font-medium">900 Mbps</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-16">Ping</span>
                  <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: '15%' }}
                      transition={{ duration: 1.5, delay: 1.2 }}
                      className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full"
                    />
                  </div>
                  <span className="text-xs text-green-500 font-medium">3 ms</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-3 bg-brand-blue rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
